import { useQuery } from "@tanstack/react-query";
import {
    UsersApiParams,
    UsersApiResponse,
    UserFullInfo,
    Role,
} from "../types/user";
import axios from "axios";
import Cookies from "js-cookie";
// Helpers
const fallbackBaseUrl = () =>
    process.env.REACT_APP_API_BASE_URL || "https://alny.shiftcms.net/api/v1";
const fallbackAuthHeaders = () => {
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;
    return {
        Authorization: `Bearer ${token}`,
        apiKey: apiKey,
        "Content-Type": "application/json",
    };
};
const baseUrlFn = fallbackBaseUrl;
const authHeadersFn = fallbackAuthHeaders;

export const useUsers = (params: UsersApiParams = {}) => {
    return useQuery({
        queryKey: ["users", params],
        queryFn: async (): Promise<UsersApiResponse> => {
            const searchParams = new URLSearchParams();

            if (params.search) searchParams.append("search", params.search);
            if (params.role) searchParams.append("role", params.role);
            if (params.access_status !== undefined)
                searchParams.append("access_status", params.access_status.toString());
            if (params.account_type !== undefined)
                searchParams.append("account_type", params.account_type.toString());
            if (params.country) searchParams.append("country", params.country);
            if (params.sex) searchParams.append("sex", params.sex);
            if (params.member_fee_filter)
                searchParams.append("member_fee_filter", params.member_fee_filter);
            if (params.per_page)
                searchParams.append("per_page", params.per_page.toString());
            if (params.page)
                searchParams.append("page", params.page.toString());
            if (params.sort_by)
                searchParams.append("sort_by", params.sort_by);
            if (params.sort_order)
                searchParams.append("sort_order", params.sort_order);

            const response = await axios.get(
                `${baseUrlFn()}/users?${searchParams.toString()}`,
                {
                    headers: authHeadersFn(),
                }
            );
            return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 минут
    });
};

export const useUserRoles = () => {
    return useQuery({
        queryKey: ["user-roles"],
        queryFn: async (): Promise<string[]> => {
            const baseUrl =
                baseUrlFn();

            const response = await axios.get(`${baseUrl}/users/roles`, {
                headers: authHeadersFn(),
            });
            return response.data;
        },
        staleTime: 10 * 60 * 1000, // 10 минут - роли редко меняются
    });
};

export const useUserFullInfo = (userId: number | null) => {
    return useQuery({
        queryKey: ["user-full-info", userId],
        queryFn: async (): Promise<UserFullInfo> => {
            if (!userId) throw new Error("User ID is required");

            const response = await axios.get(
                `${baseUrlFn()}/users/${userId}/full`,
                {
                    headers: authHeadersFn(),
                }
            );

            return response.data.data;
        },
        enabled: !!userId,
        staleTime: 5 * 60 * 1000, // 5 минут
    });
};

// --- VIP ledger (ТЗ VIP-ledger §11) ------------------------------------------

export type VipGrantMode = "days" | "until" | "forever";

export interface VipSegmentRow {
    id: number;
    source: string;
    /** Людський підпис з API (Mill/Hub UI — показувати це, не raw source) */
    label?: string;
    sku?: string | null;
    duration_days?: number | null;
    remaining_days?: number | null;
    is_forever: boolean;
    status: string;
    activated_at?: string | null;
    expires_at?: string | null;
    revoked_at?: string | null;
    revoke_reason?: string | null;
    meta?: Record<string, unknown> | null;
    created_at?: string | null;
}

export interface VipStateResponse {
    user_id: number;
    mode: "until" | "forever" | "none";
    vip: boolean;
    vip_expires_at: string | null;
    vip_forever: boolean;
    hub_vip_version: number;
    /** автосписание structural с баланса (тогл Hub/Mill); не Stripe autopay */
    structural_autopay?: boolean;
    segments?: VipSegmentRow[];
    segment_id?: number | null;
    game_sync?: {
        status: "synced" | "pending";
        pending: boolean;
    };
}

export const useUserVip = (userId: number | null) => {
    return useQuery({
        queryKey: ["user-vip", userId],
        queryFn: async (): Promise<VipStateResponse> => {
            if (!userId) throw new Error("User ID is required");

            const response = await axios.get(
                `${baseUrlFn()}/admin/users/${userId}/vip`,
                { headers: authHeadersFn() }
            );

            return response.data;
        },
        enabled: !!userId,
    });
};

export const grantVip = async (
    userId: number,
    body: {
        mode: VipGrantMode;
        days?: number;
        expires_on?: string;
        source?: string;
        comment?: string;
    }
): Promise<VipStateResponse> => {
    if (!userId) throw new Error("User ID is required");

    const response = await axios.post(
        `${baseUrlFn()}/admin/users/${userId}/vip/grant`,
        body,
        { headers: authHeadersFn() }
    );

    return response.data;
};

export const revokeVipSegment = async (
    userId: number,
    segmentId: number,
    reason?: string
): Promise<VipStateResponse> => {
    if (!userId) throw new Error("User ID is required");

    const response = await axios.post(
        `${baseUrlFn()}/admin/users/${userId}/vip/segments/${segmentId}/revoke`,
        reason ? { reason } : {},
        { headers: authHeadersFn() }
    );

    return response.data;
};

export const setStructuralAutopay = async (
    userId: number,
    enabled: boolean
): Promise<VipStateResponse> => {
    if (!userId) throw new Error("User ID is required");

    const response = await axios.post(
        `${baseUrlFn()}/admin/users/${userId}/vip/structural-autopay`,
        { structural_autopay: enabled },
        { headers: authHeadersFn() }
    );

    return response.data;
};

/** VIP ledger API — див. useUserVip / grantVip / revokeVipSegment */

export const restoreUser = async (userId: number) => {
    if (!userId) throw new Error("User ID is required");
    const baseUrl = baseUrlFn();
    const headers = authHeadersFn();
    const response = await axios.post(
        `${baseUrl}/users/${userId}/restore`,
        {},
        { headers }
    );
    return response.data;
};

export const manualDeleteUser = async (userId: number) => {
    if (!userId) throw new Error("User ID is required");
    const baseUrl = baseUrlFn();
    const headers = authHeadersFn();
    const response = await axios.post(
        `${baseUrl}/users/${userId}/manual-delete`,
        {},
        { headers }
    );
    return response.data;
};

export const bulkManualDeleteRestrictedUsers = async (): Promise<{
    success: boolean;
    deleted_count: number;
    message?: string;
}> => {
    const baseUrl = baseUrlFn();
    const headers = authHeadersFn();
    const response = await axios.post(
        `${baseUrl}/users/bulk-manual-delete-restricted`,
        {},
        { headers }
    );
    return response.data;
};

export interface TreeNode {
    id: number;
    name: string;
    email?: string;
    path?: number[];
    children?: TreeNode[];
}

export const useUserTree = (userId: number | null) => {
    return useQuery({
        queryKey: ["user-tree", userId],
        queryFn: async (): Promise<TreeNode> => {
            if (!userId) throw new Error("User ID is required");

            const baseUrl =
                process.env.REACT_APP_API_BASE_URL ||
                "https://alny.shiftcms.net/api/v1";
            const token = Cookies.get("token");
            const apiKey = process.env.REACT_APP_API_KEY;

            const response = await axios.get(
                `${baseUrl}/tree/${userId}/descendants-tree`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        apiKey: apiKey,
                        "Content-Type": "application/json",
                    },
                }
            );

            return response.data;
        },
        enabled: !!userId,
        staleTime: 2 * 60 * 1000, // 2 минуты
    });
};

export const moveUserNode = async (fromUserId: number, toParentId: number) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/tree-test/move-subtree/${fromUserId}/${toParentId}`,
        {},
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const deleteUserNode = async (userId: number) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.delete(
        `${baseUrl}/tree-test/delete-node/${userId}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const liftUserSubtree = async (userId: number) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/tree-test/lift-subtree/${userId}`,
        {},
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const getAvailableSlots = async (userId: number) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.get(
        `${baseUrl}/tree-test/available-slots/${userId}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

// API ключи
export interface ApiKey {
    id: number;
    name: string;
    key: string;
    permissions: string[];
    last_used_at?: string;
    expires_at?: string;
    created_at: string;
    updated_at: string;
}

export const useApiKeys = () => {
    return useQuery({
        queryKey: ["api-keys"],
        queryFn: async (): Promise<ApiKey[]> => {
            const baseUrl =
                process.env.REACT_APP_API_BASE_URL ||
                "https://alny.shiftcms.net/api/v1";
            const token = Cookies.get("token");
            const apiKey = process.env.REACT_APP_API_KEY;

            const response = await axios.get(`${baseUrl}/api-keys`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    apiKey: apiKey,
                    "Content-Type": "application/json",
                },
            });

            // Убеждаемся, что возвращаем массив
            const data = response.data;
            if (Array.isArray(data)) {
                return data;
            } else if (data && Array.isArray(data.data)) {
                return data.data;
            } else {
                console.warn("Unexpected API response format:", data);
                return [];
            }
        },
        staleTime: 2 * 60 * 1000, // 2 минуты
    });
};

export const createApiKey = async (name: string, permissions: string[]) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/api-keys`,
        {
            name,
            permissions,
        },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const deleteApiKey = async (id: number) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.delete(`${baseUrl}/api-keys/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            apiKey: apiKey,
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

// Системные настройки
export interface Setting {
    id: number;
    key: string;
    value: string | number | boolean | object;
    type: "string" | "int" | "bool" | "json";
    description: string;
    updated_at: string;
}

export interface SettingGroup {
    name: string;
    settings: Setting[];
}

export interface NewSetting {
    key: string;
    value: string;
    type: "string" | "int" | "bool" | "json";
    description: string;
}

export interface JsonField {
    key: string;
    value: any;
    type: "string" | "number" | "boolean";
}

export const useSettings = () => {
    return useQuery({
        queryKey: ["settings"],
        queryFn: async (): Promise<Setting[]> => {
            const baseUrl =
                process.env.REACT_APP_API_BASE_URL ||
                "https://alny.shiftcms.net/api/v1";
            const token = Cookies.get("token");
            const apiKey = process.env.REACT_APP_API_KEY;

            const response = await axios.get(`${baseUrl}/settings`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    apiKey: apiKey,
                    "Content-Type": "application/json",
                },
            });

            // Убеждаемся, что возвращаем массив
            const data = response.data;
            if (Array.isArray(data)) {
                return data;
            } else if (data && Array.isArray(data.data)) {
                return data.data;
            } else if (data && Array.isArray(data.settings)) {
                return data.settings;
            } else {
                console.warn("Unexpected API response format:", data);
                return [];
            }
        },
        staleTime: 2 * 60 * 1000, // 2 минуты
    });
};

export const createSetting = async (setting: NewSetting) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(`${baseUrl}/settings`, setting, {
        headers: {
            Authorization: `Bearer ${token}`,
            apiKey: apiKey,
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const updateSetting = async (id: number, setting: Partial<Setting>) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.put(`${baseUrl}/settings/${id}`, setting, {
        headers: {
            Authorization: `Bearer ${token}`,
            apiKey: apiKey,
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const deleteSetting = async (id: number) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.delete(`${baseUrl}/settings/${id}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            apiKey: apiKey,
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

// Платежи
export interface Payment {
    id: number;
    user_id: number;
    sum: number;
    total: number;
    type: number;
    type2: number | null;
    desc: string;
    additional_desc: string | null;
    time: string;
    sort: number | null;
    currency: number;
    rabotnik: number | null;
    anual: number | null;
    user: {
        id: number;
        name: string;
        email: string;
        telegram_username: string | null;
    };
    currency_name: string;
    payment_type_name: string;
}

export interface PaymentFilters {
    user_id?: string;
    user_email?: string;
    user_name?: string;
    min_amount?: string;
    max_amount?: string;
    currency?: string;
    date_from?: string;
    date_to?: string;
    type?: string;
    desc?: string;
    anual?: string;
    per_page?: number;
    page?: number;
}

export interface PaymentsResponse {
    success: boolean;
    data: Payment[];
    unique_descs: string[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export const usePayments = (filters: PaymentFilters = {}) => {
    return useQuery({
        queryKey: ["payments", filters],
        queryFn: async (): Promise<PaymentsResponse> => {
            const baseUrl =
                process.env.REACT_APP_API_BASE_URL ||
                "https://alny.shiftcms.net/api/v1";
            const token = Cookies.get("token");
            const apiKey = process.env.REACT_APP_API_KEY;

            // Формируем query параметры
            const params = new URLSearchParams();
            if (filters.user_id) params.append("user_id", filters.user_id);
            if (filters.user_email)
                params.append("user_email", filters.user_email);
            if (filters.user_name)
                params.append("user_name", filters.user_name);
            if (filters.min_amount)
                params.append("min_amount", filters.min_amount);
            if (filters.max_amount)
                params.append("max_amount", filters.max_amount);
            if (filters.currency !== undefined && filters.currency !== "")
                params.append("currency", filters.currency);
            if (filters.date_from)
                params.append("date_from", filters.date_from);
            if (filters.date_to) params.append("date_to", filters.date_to);
            if (filters.type) params.append("type", filters.type);
            if (filters.desc) params.append("desc", filters.desc);
            if (filters.anual) params.append("anual", filters.anual);
            params.append("per_page", (filters.per_page || 20).toString());
            params.append("page", (filters.page || 1).toString());

            const response = await axios.get(
                `${baseUrl}/membership/all-payments?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        apiKey: apiKey,
                        "Content-Type": "application/json",
                    },
                }
            );

            return response.data;
        },
        staleTime: 2 * 60 * 1000, // 2 минуты
    });
};

export interface CreatePaymentData {
    user_id: number;
    company_id?: number;
    order?: number;
    orders_unity_id?: string;
    payze_id?: number;
    sum: number;
    total: number;
    type: number;
    type2: number;
    desc: string;
    additional_desc?: string;
    time: string;
    sort: number;
    currency: number;
    rabotnik?: number;
    anual?: string;
}

export const createPayment = async (data: CreatePaymentData) => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/admin/payments/create`,
        data,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

// API функции для управления ролями
export const getRoles = async (): Promise<Role[]> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.get(`${baseUrl}/users/roles`, {
        headers: {
            Authorization: `Bearer ${token}`,
            apiKey: apiKey,
            "Content-Type": "application/json",
        },
    });

    // Проверяем, если данные в обертке data
    const data = response.data.data || response.data;

    return data;
};

export const createRole = async (roleData: {
    name: string;
    guard_name?: string;
}): Promise<Role> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(`${baseUrl}/users/roles`, roleData, {
        headers: {
            Authorization: `Bearer ${token}`,
            apiKey: apiKey,
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const updateRole = async (
    roleId: number,
    roleData: { name?: string; guard_name?: string }
): Promise<Role> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.put(
        `${baseUrl}/users/roles/${roleId}`,
        roleData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const assignRoleToUser = async (
    userId: number,
    role: string,
    guard_name?: string
): Promise<{ message: string }> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/users/${userId}/assign-role`,
        { role, guard_name },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const removeRoleFromUser = async (
    userId: number,
    role: string,
    guard_name?: string
): Promise<{ message: string }> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/users/${userId}/remove-role`,
        { role, guard_name },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const syncUserRoles = async (
    userId: number,
    roles: string[],
    guard_name?: string
): Promise<{ message: string }> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.post(
        `${baseUrl}/users/${userId}/sync-roles`,
        { roles, guard_name },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const updateUser = async (
    userId: number,
    userData: {
        name?: string;
        surname?: string;
        email?: string;
        mobile?: string;
        telegram_id?: string | null;
        telegram_username?: string | null;
        sex?: number | null;
        passport?: string | null;
        bank_account?: string | null;
        bank_swift?: string | null;
        bank_card?: string | null;
        bep20?: string | null;
        trc20?: string | null;
        user_credit_history?: number;
        inviter_id?: number | null;
        member_fee_time?: string | null;
        access_status?: number;
        can_change_telegram?: boolean;
        is_demo?: boolean;
        heir_name?: string | null;
        heir_surname?: string | null;
        heir_passport?: string | null;
        heir_dob?: string | null;
    }
): Promise<{ message: string }> => {
    const baseUrl =
        process.env.REACT_APP_API_BASE_URL ||
        "https://alny.shiftcms.net/api/v1";
    const token = Cookies.get("token");
    const apiKey = process.env.REACT_APP_API_KEY;

    const response = await axios.put(
        `${baseUrl}/users/${userId}`,
        userData,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                apiKey: apiKey,
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const clearUserTokens = async (userId: number) => {
    if (!userId) throw new Error("User ID is required");
    const baseUrl = baseUrlFn();
    const headers = authHeadersFn();
    const response = await axios.post(
        `${baseUrl}/users/${userId}/clear-user-tokens`,
        {},
        { headers }
    );
    return response.data;
};
